import React, { useEffect, useState, useRef } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import { IoSettings } from "react-icons/io5";
import SlippageSettings from "./SlippageSettings";
import { useConnectWallet } from "../hooks/useConnectWallet";
import { PendingLabel } from "./Spinner";
import { useParams, useNavigate } from "react-router-dom";
import { useActiveAccount, useActiveWalletChain } from "thirdweb/react";
import {
    getErc20Contract,
    getLPContract,
    getRouterContract,
} from "../contexts/thirdWebClient";
import {
    allowance,
    approve,
    balanceOf,
    getCurrencyMetadata,
} from "thirdweb/extensions/erc20";
import { list as tokenList } from "../contexts/SelectTokenDATA";
import {
    prepareContractCall,
    readContract,
    sendTransaction as sendTx,
} from "thirdweb";
import { ethers } from "ethers";
import { routerAddress, NETWORK_LABEL } from "../contexts/config";
import { safeParseUnits } from "../utility/amount";
import { toast } from "react-toastify";

const getTokenIcon = (address) => {
    const token = tokenList.find(
        (t) => t.address.toLowerCase() === address?.toLowerCase(),
    );
    return token?.img || null;
};

const RemoveLiquidity = () => {
    const navigate = useNavigate();
    const [value, setValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [slippage, setSlippage] = useState(() => {
        const saved = localStorage.getItem("usdx_slippage");
        return saved ? parseFloat(saved) : 1;
    });

    const { pairAddress} = useParams();

    const add = useActiveAccount();
    const [address, setAddress] = useState("");
    const chain = useActiveWalletChain();
    const { openConnect, isConnecting } = useConnectWallet();

    useEffect(() => {
        if (add && add.address && chain && pairAddress) {
            setAddress(add.address);
            fetchLpDetails(pairAddress, add.address, chain);
        } else if (!add?.address) {
            setAddress("");
        }
    }, [add, chain, pairAddress]);

    const [lpDetails, setLpDetails] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);

    /* Placeholders only shimmer while a fetch is actually in flight. Position
       details are read per account, so with no wallet they would otherwise
       pulse forever on a page that is waiting for the user, not the chain. */
    const shimmer = (extra = "") => `${extra} ${address ? "animate-pulse" : ""}`;

    const fetchLpDetails = async (pairAddress, address, chain) => {
        try {
            setDataLoaded(false);
            const contract = getErc20Contract(pairAddress, chain);
            let currencyMetadata = await getCurrencyMetadata({ contract });
            let pairTotalSupply = await readContract({
                contract,
                method: "function totalSupply() view returns (uint256)",
                params: [],
            });
            let userBalance = await balanceOf({
                contract,
                address: address,
            });
            let approvedAmount = await allowance({
                contract,
                owner: address,
                spender: routerAddress,
            });

            const lpContract = getLPContract(pairAddress, chain);

            let token0Address = await readContract({
                contract: lpContract,
                method: "function token0() view returns (address)",
                params: [],
            });
            let token1Address = await readContract({
                contract: lpContract,
                method: "function token1() view returns (address)",
                params: [],
            });

            const Token0contract = getErc20Contract(token0Address, chain);

            let tokenOMetaData = await getCurrencyMetadata({
                contract: Token0contract,
            });
            let tokenOUserBalance = await balanceOf({
                contract: Token0contract,
                address: pairAddress,
            });

            const Token1contract = getErc20Contract(token1Address, chain);
            let token1MetaData = await getCurrencyMetadata({
                contract: Token1contract,
            });
            let token1UserBalance = await balanceOf({
                contract: Token1contract,
                address: pairAddress,
            });

            let userShare =
                (Number(
                    ethers.formatUnits(userBalance, currencyMetadata?.decimals),
                ) *
                    100) /
                Number(
                    ethers.formatUnits(
                        pairTotalSupply,
                        currencyMetadata?.decimals,
                    ),
                );

            const routerContract = await getRouterContract(chain);
            let token0Price = await readContract({
                contract: routerContract,
                method: "function quote(uint256 amountA, uint256 reserveA, uint256 reserveB) pure returns (uint256 amountB)",
                params: [
                    ethers.parseUnits("1", tokenOMetaData?.decimals),
                    tokenOUserBalance,
                    token1UserBalance,
                ],
            });
            let token1Price = await readContract({
                contract: routerContract,
                method: "function quote(uint256 amountA, uint256 reserveA, uint256 reserveB) pure returns (uint256 amountB)",
                params: [
                    ethers.parseUnits("1", token1MetaData?.decimals),
                    token1UserBalance,
                    tokenOUserBalance,
                ],
            });

            let pairData = {
                pairAddress: pairAddress,
                token0Address,
                token1Address,
                currencyMetadata,
                token0Price: Number(
                    ethers.formatUnits(token0Price, token1MetaData?.decimals),
                ),
                token1Price: Number(
                    ethers.formatUnits(token1Price, tokenOMetaData?.decimals),
                ),
                lpName:
                    tokenOMetaData?.symbol +
                    "-" +
                    token1MetaData?.symbol +
                    " LP",
                lpTotalSupply: Number(
                    ethers.formatUnits(
                        pairTotalSupply,
                        currencyMetadata?.decimals,
                    ),
                ),
                approvedBalance: Number(
                    ethers.formatUnits(
                        approvedAmount,
                        currencyMetadata?.decimals,
                    ),
                ),
                lpUserBalance: Number(
                    ethers.formatUnits(userBalance, currencyMetadata?.decimals),
                ),
                userShare: userShare,
                tokenOMetaData,
                token1MetaData,
                token0Name: tokenOMetaData?.name,
                token0Symbol: tokenOMetaData?.symbol,
                token0UserBalance:
                    (userShare *
                        Number(
                            ethers.formatUnits(
                                tokenOUserBalance,
                                tokenOMetaData?.decimals,
                            ),
                        )) /
                    100,
                token1Name: token1MetaData?.name,
                token1Symbol: token1MetaData?.symbol,
                token1UserBalance:
                    (userShare *
                        Number(
                            ethers.formatUnits(
                                token1UserBalance,
                                token1MetaData?.decimals,
                            ),
                        )) /
                    100,
            };
            console.log("%c Line:85 🍰 pairData", "color:#6ec1c2", pairData);
            setLpDetails(pairData);
            setDataLoaded(true);
        } catch (error) {
            setDataLoaded(false);
            console.log("%c Line:47 🍉 error", "color:#42b983", error);
        }
    };

    // Guards every wallet-facing action against re-entry. A ref rather than
    // `loading`, because state does not update until React re-renders — a fast
    // second click would otherwise open a second wallet request.
    const inFlight = useRef(false);

    const approveTokenFun = async (tokenAddress) => {
        if (inFlight.current) return;
        inFlight.current = true;
        try {
            setLoading(true);
            // const { mutate: sendTransaction } = useSendTransaction();
            const contract = getErc20Contract(tokenAddress, chain);
            const transaction = approve({
                contract,
                spender: routerAddress,
                amount: 1000,
            });
            console.log(
                "%c Line:117 🍢 transaction",
                "color:#ed9ec7",
                transaction,
                add,
            );
            const tx = await sendTx({ transaction, account: add });
            console.log("%c Line:118 🥔 tx", "color:#93c0a4", tx);
            toast.success("Token approved");
            // Refetch LP details so approvedBalance updates and button switches
            await fetchLpDetails(pairAddress, address, chain);
        } catch (error) {
            toast.error(error?.message);
            console.log("%c Line:111 🍫 error", "color:#ed9ec7", error);
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    const [token0Amount, setToken0Amount] = useState(0);
    const [token1Amount, setToken1Amount] = useState(0);

    const updateValue = (value) => {
        let val = parseFloat(value);
        setValue(value);
        let tokenAmount = (val * lpDetails.token0UserBalance) / 100;
        setToken0Amount(tokenAmount);
        let tokenBAmount = (val * lpDetails.token1UserBalance) / 100;
        setToken1Amount(tokenBAmount);
    };

    // Calculate minimum amount with slippage
    const calculateMinAmount = (amount, decimals) => {
        try {
            const slippageMultiplier = (100 - slippage) / 100;
            const minAmount = parseFloat(amount) * slippageMultiplier;
            return safeParseUnits(minAmount, decimals);
        } catch (error) {
            console.log("Error calculating min amount:", error);
            return "1"; // Fallback
        }
    };

    const removeLiquidity = async () => {
        // A ref, not `loading`: state does not update until React re-renders, so
        // a fast second click would otherwise open a second wallet request.
        if (inFlight.current) return;
        if (value <= 0) {
            toast.error("Please Enter Valid amount");
            return;
        }
        inFlight.current = true;
        setLoading(true);
        try {
            console.log("%c Line:236 🥕 value", "color:#b03734", value);
            const contract = getRouterContract(chain);

            // `amount` is float maths, so it stringifies to up to 20 fraction
            // digits — more than the LP token's 18 — which parseUnits rejects.
            let amount = (value * lpDetails.lpUserBalance) / 100;
            let amountPair = safeParseUnits(
                amount,
                lpDetails?.currencyMetadata?.decimals,
            );
            const transaction = prepareContractCall({
                contract,
                method: "function removeLiquidity(address tokenA, address tokenB, uint256 liquidity, uint256 amountAMin, uint256 amountBMin, address to, uint256 deadline) returns (uint256 amountA, uint256 amountB)",
                params: [
                    lpDetails?.token0Address,
                    lpDetails?.token1Address,
                    amountPair,
                    calculateMinAmount(token0Amount, lpDetails?.tokenOMetaData?.decimals),
                    calculateMinAmount(token1Amount, lpDetails?.token1MetaData?.decimals),
                    address,
                    Math.floor(new Date().getTime() / 1000) + 900,
                ],
            });
            console.log(
                "%c Line:280 🧀 transaction",
                "color:#465975",
                transaction,
            );
            const txData = await sendTx({ transaction, account: add });
            console.log("%c Line:173 🥐 txData", "color:#3f7cff", txData);
            toast.success("Liquidity Removed Successfully");
            setLoading(false);

            // Navigate back to pool page with refresh state
            navigate('/pool', { state: { refresh: true } });
        } catch (error) {
            toast.error(error?.message);
            console.log("%c Line:166 🍕 error", "color:#f5ce50", error);
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    /* Row in the position panel; em dash until the pair data has loaded. */
    const Detail = ({ label, value: v, accent }) => (
        <div className="flex items-baseline justify-between gap-3 py-2.5 border-b border-usdx-line-soft last:border-b-0">
            <span className="text-[12px] text-usdx-ink-subtle font-medium shrink-0">{label}</span>
            <span
                className={`num text-[12px] font-semibold text-right truncate ${
                    accent ? "text-usdx-accent" : "text-usdx-ink"
                }`}
            >
                {v ?? "—"}
            </span>
        </div>
    );

    return (
        <div className="w-full max-w-[1000px] grid grid-cols-1 lg:grid-cols-[minmax(0,470px)_minmax(0,1fr)] gap-4 lg:gap-5 items-start justify-center">
            <div className="w-full flex flex-col card-3d p-4 sm:p-6">
            {/* Header */}
            <div className="flex justify-between items-center gap-2 mb-5">
                <button
                    className="swap-arrow-3d w-9 h-9 flex items-center justify-center"
                    onClick={() => navigate('/pool')}
                >
                    <FaArrowLeft className="size-[14px] text-usdx-ink-muted" />
                </button>
                <h2 className="text-[18px] xs:text-[20px] sm:text-[22px] font-bold gradient-text text-center">
                    Remove Liquidity
                </h2>
                <button
                    className="swap-arrow-3d w-9 h-9 flex items-center justify-center"
                    onClick={() => setSettingsOpen(true)}
                    title="Slippage Settings"
                >
                    <IoSettings className="size-[18px] text-usdx-ink-muted" />
                </button>
            </div>

            {/* Remove Amount Section */}
            <div className="inner-3d p-4 sm:p-5 mb-3">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest">
                        Remove Amount
                    </span>
                </div>
                <p className="num text-[48px] xs:text-[56px] sm:text-[64px] text-left font-bold gradient-text leading-none mb-3">
                    {value}%
                </p>
                <input
                    type="range"
                    min="1"
                    max="100"
                    value={value}
                    disabled={!dataLoaded}
                    className={`w-full outline-none slider ${!dataLoaded ? 'opacity-40 cursor-not-allowed' : ''}`}
                    style={{
                        background: `linear-gradient(to right, rgb(var(--u-accent)) ${value}%, rgb(var(--u-line-strong) / 0.6) ${value}%)`,
                    }}
                    onChange={(e) => dataLoaded && updateValue(e.target.value)}
                />
                <div className="flex justify-between items-center w-full mt-4 gap-2">
                    {["25", "50", "75", "100"].map((v) => (
                        <button
                            key={v}
                            disabled={!dataLoaded}
                            onClick={() => dataLoaded && updateValue(v)}
                            className={`num flex-1 text-[13px] font-bold py-2.5 rounded-xl transition-all ${
                                !dataLoaded
                                    ? "inner-3d text-usdx-ink-subtle cursor-not-allowed opacity-50"
                                    : value === v
                                    ? "btn-3d"
                                    : "inner-3d text-usdx-ink-muted hover:text-usdx-accent"
                            }`}
                        >
                            {v === "100" ? "Max" : `${v}%`}
                        </button>
                    ))}
                </div>
            </div>

            {/* Arrow, in the gap between the panels rather than over them */}
            <div className="flex justify-center py-2">
                <div className="swap-arrow-3d w-10 h-10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-usdx-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                </div>
            </div>

            {/* Output Section */}
            <div className="inner-3d p-4 mb-3">
                {!dataLoaded ? (
                    <>
                        <div className="flex items-center justify-between py-2">
                            <div className={shimmer("h-5 w-20 rounded bg-usdx-surface/60")} />
                            <div className="flex gap-2 items-center">
                                <div className={shimmer("size-[20px] rounded-full bg-usdx-surface/60")} />
                                <div className={shimmer("h-4 w-12 rounded bg-usdx-surface/60")} />
                            </div>
                        </div>
                        <div className="border-t border-usdx-line my-1"></div>
                        <div className="flex items-center justify-between py-2">
                            <div className={shimmer("h-5 w-20 rounded bg-usdx-surface/60")} />
                            <div className="flex gap-2 items-center">
                                <div className={shimmer("size-[20px] rounded-full bg-usdx-surface/60")} />
                                <div className={shimmer("h-4 w-12 rounded bg-usdx-surface/60")} />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex items-center justify-between py-2">
                            <span className="num text-usdx-ink text-[18px] font-bold">
                                {token0Amount.toFixed(3)}
                            </span>
                            <div className="flex gap-2 items-center">
                                {getTokenIcon(lpDetails?.token0Address) ? (
                                    <img src={getTokenIcon(lpDetails?.token0Address)} alt={lpDetails?.token0Symbol} className="size-[20px] rounded-full ring-1 ring-usdx-line" />
                                ) : (
                                    <div className="size-[20px] rounded-full bg-usdx-accent/20 flex items-center justify-center text-[10px] font-bold text-usdx-accent ring-1 ring-usdx-line">
                                        {lpDetails?.token0Symbol?.[0] || '?'}
                                    </div>
                                )}
                                <span className="text-usdx-ink text-[16px] font-bold">
                                    {lpDetails?.token0Symbol}
                                </span>
                            </div>
                        </div>
                        <div className="border-t border-usdx-line my-1"></div>
                        <div className="flex items-center justify-between py-2">
                            <span className="num text-usdx-ink text-[18px] font-bold">
                                {token1Amount.toFixed(3)}
                            </span>
                            <div className="flex gap-2 items-center">
                                {getTokenIcon(lpDetails?.token1Address) ? (
                                    <img src={getTokenIcon(lpDetails?.token1Address)} alt={lpDetails?.token1Symbol} className="size-[20px] rounded-full ring-1 ring-usdx-line" />
                                ) : (
                                    <div className="size-[20px] rounded-full bg-usdx-accent/20 flex items-center justify-center text-[10px] font-bold text-usdx-accent ring-1 ring-usdx-line">
                                        {lpDetails?.token1Symbol?.[0] || '?'}
                                    </div>
                                )}
                                <span className="text-usdx-ink text-[16px] font-bold">
                                    {lpDetails?.token1Symbol}
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 w-full">
                {!address ? (
                    /* The skeleton below never resolves without an account —
                       position details are read per address — so ask for one. */
                    <button
                        className="w-full py-4 text-[16px] font-bold btn-3d"
                        disabled={isConnecting}
                        onClick={openConnect}
                    >
                        {isConnecting ? (
                            <PendingLabel>Connecting…</PendingLabel>
                        ) : (
                            "Connect wallet"
                        )}
                    </button>
                ) : !dataLoaded ? (
                    <div className={shimmer("w-full py-4 rounded-xl bg-usdx-surface/60 h-[56px]")} />
                ) : lpDetails && lpDetails?.approvedBalance <= 0 ? (
                    <button
                        className="w-full py-4 text-[16px] font-bold btn-3d"
                        disabled={loading}
                        onClick={() => !loading && approveTokenFun(lpDetails?.pairAddress)}
                    >
                        {loading ? <PendingLabel>Approving…</PendingLabel> : "Approve"}
                    </button>
                ) : (
                    ""
                )}
                {address && dataLoaded && lpDetails?.approvedBalance > 0 && (
                    <button
                        className="w-full py-4 text-[16px] font-bold btn-3d"
                        disabled={loading || !lpDetails?.pairAddress || value === 0}
                        onClick={removeLiquidity}
                    >
                        {loading ? (
                            <PendingLabel>Removing…</PendingLabel>
                        ) : (
                            "Remove liquidity"
                        )}
                    </button>
                )}
            </div>
                <SlippageSettings
                    open={settingsOpen}
                    setOpen={setSettingsOpen}
                    slippage={slippage}
                    setSlippage={setSlippage}
                />
            </div>

            {/* ── Position ───────────────────────────────────────────────── */}
            <aside className="w-full flex flex-col gap-4">
                <div className="card-3d p-4 sm:p-5">
                    <h3 className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest mb-1">
                        Your position
                    </h3>
                    <div className="flex flex-col">
                        <Detail label="Pair" value={dataLoaded ? lpDetails?.lpName : null} />
                        <Detail
                            label="Pool share"
                            value={dataLoaded ? `${lpDetails?.userShare?.toFixed(4)}%` : null}
                            accent
                        />
                        <Detail
                            label="LP tokens"
                            value={dataLoaded ? lpDetails?.lpUserBalance?.toFixed(6) : null}
                        />
                        <Detail label="Removing" value={dataLoaded ? `${value}%` : null} accent />
                        <Detail label="Slippage tolerance" value={`${slippage}%`} />
                        <Detail label="Network" value={NETWORK_LABEL} />
                    </div>
                </div>

                {/* Both directions, since either can be the useful one */}
                <div className="card-3d p-4 sm:p-5">
                    <h3 className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest mb-3">
                        Pool price
                    </h3>
                    {!dataLoaded ? (
                        <div className="flex flex-col gap-2">
                            <div className={shimmer("h-4 w-full rounded bg-usdx-surface-sunken")} />
                            <div className={shimmer("h-4 w-4/5 rounded bg-usdx-surface-sunken")} />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <div className="pool-card-3d px-3 py-2.5">
                                <p className="num text-[12px] font-medium text-usdx-ink">
                                    1 {lpDetails?.token0Symbol} = {lpDetails?.token0Price?.toFixed(3)}{" "}
                                    {lpDetails?.token1Symbol}
                                </p>
                            </div>
                            <div className="pool-card-3d px-3 py-2.5">
                                <p className="num text-[12px] font-medium text-usdx-ink">
                                    1 {lpDetails?.token1Symbol} = {lpDetails?.token1Price?.toFixed(3)}{" "}
                                    {lpDetails?.token0Symbol}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {value === 100 || value === "100" ? (
                    <div className="card-3d p-4 sm:p-5">
                        <p className="text-[12px] font-bold text-usdx-warning mb-1.5">
                            Closing the whole position
                        </p>
                        <p className="text-[12px] text-usdx-ink-subtle leading-relaxed">
                            Removing 100% burns all of your LP tokens for this pair. You stop
                            earning fees on it, and re-entering later means depositing at
                            whatever the ratio is then.
                        </p>
                    </div>
                ) : null}
            </aside>

            {/* ── How it works + things to know ──────────────────────────── */}
            <section className="lg:col-span-2 w-full card-3d p-4 sm:p-6">
                <h3 className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest mb-4 text-left">
                    How it works
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                        {
                            step: "1",
                            title: "Choose how much",
                            desc: "Pick a percentage of your position. The amounts you get back are calculated from the pool's current reserves.",
                        },
                        {
                            step: "2",
                            title: "Approve the LP token",
                            desc: "The router needs a one-off allowance to burn your LP tokens. Only required the first time for this pair.",
                        },
                        {
                            step: "3",
                            title: "Withdraw the pair",
                            desc: "Both underlying tokens return to your wallet in a single transaction, including the fees your share has accrued.",
                        },
                    ].map((item) => (
                        <div key={item.step} className="pool-card-3d p-4 flex flex-col gap-2 text-left">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-usdx-accent/10 text-usdx-accent text-[12px] font-bold shrink-0">
                                {item.step}
                            </div>
                            <p className="text-[13px] font-bold text-usdx-ink">{item.title}</p>
                            <p className="text-[11px] text-usdx-ink-subtle leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>

                <h3 className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest mt-6 mb-4 text-left">
                    Things to know
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                        {
                            title: "You always get both tokens",
                            desc: "Withdrawing returns the pair in the pool's current ratio — not whichever single token you would prefer.",
                        },
                        {
                            title: "Partial withdrawals are fine",
                            desc: "Take out any percentage. What you leave behind keeps earning its share of swap fees.",
                        },
                        {
                            title: "Slippage applies here too",
                            desc: "The transaction reverts if the reserves shift enough that you would receive less than your tolerance allows.",
                        },
                        {
                            title: "The ratio may have moved",
                            desc: "If prices diverged since you deposited, the mix coming back will differ from what you put in.",
                        },
                    ].map((item) => (
                        <div key={item.title} className="pool-card-3d p-4 flex flex-col gap-1.5 text-left">
                            <p className="text-[12px] font-bold text-usdx-ink">{item.title}</p>
                            <p className="text-[11px] text-usdx-ink-subtle leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default RemoveLiquidity;
